import { useEffect, useMemo, useState } from 'react';
import { useStore } from '@/context/StoreContext';
import { ProductCard } from '@/components/products/ProductCard';
import { MEDIA } from '@/data/site';
import { navigate } from '@/hooks/useRoute';
import type { Product } from '@/types/product';
import './CatalogPage.css';

const money=(n:number)=>new Intl.NumberFormat('he-IL',{style:'currency',currency:'ILS',maximumFractionDigits:0}).format(n);
const inCategory=(p:Product,key:string)=>p.category===key||(p.categoryKeys||[]).includes(key);

// MOISSANITE_V5_CATALOG
export function CatalogPage(){
 const {products,loading,cms}=useStore();
 const params=new URLSearchParams(window.location.search);
 const category=params.get('category')||'';
 const initialQ=params.get('q')||'';
 const initialSale=params.get('sale')==='1';

 const [q,setQ]=useState(initialQ);
 const [shape,setShape]=useState('');
 const [stoneSize,setStoneSize]=useState('');
 const [max,setMax]=useState(999999);
 const [stock,setStock]=useState(false);
 const [ready,setReady]=useState(false);
 const [sale,setSale]=useState(initialSale);
 const [tennis,setTennis]=useState(/טניס|tennis/i.test(initialQ));
 const [selected,setSelected]=useState(false);
 const [sort,setSort]=useState('featured');
 const [visible,setVisible]=useState(24);
 const [mobileFilters,setMobileFilters]=useState(false);

 // MIELLEY_SEARCH_ROUTE_SYNC_V512
 useEffect(()=>{
  setQ(initialQ);
  setTennis(/tennis/i.test(initialQ) || initialQ.includes('\u05d8\u05e0\u05d9\u05e1'));
  setVisible(24);
 },[initialQ]);

 const activeProducts=useMemo(()=>products.filter(p=>p.status==='active'&&p.price>0),[products]);
 const catalogCeiling=useMemo(()=>{
  const highest=Math.max(0,...activeProducts.map(p=>Number(p.price)||0));
  return Math.max(5000,Math.ceil(highest/500)*500);
 },[activeProducts]);

 const cleanLabel=(value:string)=>String(value||'')
  .replace(/טבעות אירוסין/g,'טבעות מוסונייט')
  .replace(/טבעת אירוסין/g,'טבעת מוסונייט')
  .replace(/טבעות נישואין/g,'טבעות מוסונייט')
  .replace(/יהלומי מעבדה|יהלום מעבדה|יהלומים|יהלום/g,'מוסונייט')
  .replace(/MIELLEY/g,'')
  .trim();

 const categoryMatch=(p:any,key:string)=>{
  if(!key)return true;
  if(key==='new')return true;
  if(key==='rings')return inCategory(p,'rings')||inCategory(p,'engagement')||inCategory(p,'wedding');
  if(key==='ready')return Boolean(p.readyToShip)||inCategory(p,'ready');
  return inCategory(p,key);
 };

 const searchable=(p:any)=>[
  p.name,p.categoryLabel,p.subcategory,p.shape,p.mielleySku,p.carat,
  ...(p.sourceCategories||[])
 ].filter(Boolean).join(' ').toLowerCase();

 const isTennis=(p:any)=>/טניס|tennis/i.test(searchable(p));

 const sourceForOptions=useMemo(
  ()=>activeProducts.filter(p=>categoryMatch(p,category)),
  [activeProducts,category]
 );

 const shapes=useMemo(
  ()=>[...new Set(sourceForOptions.map(p=>p.shape).filter(Boolean))].sort(),
  [sourceForOptions]
 );

 const stoneSizes=useMemo(
  ()=>[...new Set(sourceForOptions.map(p=>p.carat).filter(Boolean))].sort((a,b)=>
    (parseFloat(String(a))||0)-(parseFloat(String(b))||0)
  ),
  [sourceForOptions]
 );

 const items=useMemo(()=>{
  let out=activeProducts.filter(p=>categoryMatch(p,category));

  if(q){
   const z=q.toLowerCase().trim();
   if(z)out=out.filter(p=>searchable(p).includes(z));
  }
  if(shape)out=out.filter(p=>p.shape===shape);
  if(stoneSize)out=out.filter(p=>p.carat===stoneSize);
  if(stock)out=out.filter(p=>p.stockQty>0||p.readyToShip);
  if(ready)out=out.filter(p=>p.readyToShip||inCategory(p,'ready'));
  if(sale)out=out.filter(p=>p.oldPrice>p.price);
  if(tennis)out=out.filter(isTennis);
  if(selected)out=out.filter(p=>p.featured);
  out=out.filter(p=>p.price<=max);
  if(category==='new'&&sort==='featured')out=[...out].sort((a,b)=>Number(b.id)-Number(a.id));

  if(sort==='low')out=[...out].sort((a,b)=>a.price-b.price);
  if(sort==='high')out=[...out].sort((a,b)=>b.price-a.price);
  if(sort==='new')out=[...out].sort((a,b)=>Number(b.id)-Number(a.id));
  if(sort==='featured')out=[...out].sort((a,b)=>
    Number(b.featured)-Number(a.featured)||
    Number(b.readyToShip)-Number(a.readyToShip)
  );

  return out;
 },[activeProducts,category,q,shape,stoneSize,max,stock,ready,sale,tennis,selected,sort]);

 const isSaleLanding=!category&&sale;

const categoryInfo:any={
  new:{
    title:'חדשים באתר',
    eyebrow:'JUST ARRIVED',
    intro:'הפריטים החדשים של MIELLEY — תכשיטי מוסונייט וכסף 925 במראה יוקרתי, נקי ומדויק.'
  },
  rings:{
    title:'טבעות מוסונייט',
    eyebrow:'THE RING EDIT',
    intro:'טבעות מוסונייט אלגנטיות עם ברק נקי ומראה יקר — ליום יום, לערב ולרגעים מיוחדים.'
  },
  earrings:{
    title:'עגילי מוסונייט',
    eyebrow:'THE EARRING EDIT',
    intro:'עגילים עדינים ונוצצים שמרימים כל לוק — סטאדים, תלויים ועיצובים יוקרתיים בקו נקי.'
  },
  necklaces:{
    title:'שרשראות מוסונייט',
    eyebrow:'THE NECKLACE EDIT',
    intro:'שרשראות ותליונים שמוסיפים אור, נשיות ואלגנטיות — בעיצוב עדין שמתאים לכל רגע.'
  },
  bracelets:{
    title:'צמידי מוסונייט',
    eyebrow:'THE BRACELET EDIT',
    intro:'צמידים נוצצים עם נוכחות מדויקת וגימור יוקרתי — מושלמים לבד או בשילוב.'
  }
};
 const info=categoryInfo[category];
 const cat=category?cms.categories[category]:undefined;
const title = info?.title || (
  isSaleLanding ? 'SALE' : 'כל תכשיטי המוסונייט'
);

const intro = info?.intro || (
  isSaleLanding
    ? 'תכשיטי מוסונייט נבחרים במחירים מיוחדים — יוקרה חכמה שאי אפשר לפספס.'
    : 'הקולקציה המלאה של MIELLEY — טבעות, עגילים, שרשראות וצמידים במראה יוקרתי ובחירה חכמה.'
);
 const heroImage =
  isSaleLanding
    ? MEDIA.heroSaleV711
    : category === 'new'
      ? '/assets/new-arrivals-hero.png'
      : (!category || category === 'all')
        ? '/assets/all-moissanite-hero.png'
        : (cat?.image || MEDIA.hero);
 const heroEyebrow = info?.eyebrow || (
  isSaleLanding ? 'SPECIAL PRICES' : 'THE MOISSANITE COLLECTION'
);
 const heroClass = ' hero-unified';

 const sliderValue=Math.min(max,catalogCeiling);
 const hasFilters=Boolean(q||shape||stoneSize||stock||ready||sale||tennis||selected||max<999999);

 const clear=()=>{
  setQ('');
  setShape('');
  setStoneSize('');
  setMax(999999);
  setStock(false);
  setReady(false);
  setSale(false);
  setTennis(false);
  setSelected(false);
  setVisible(24);
 };

 const categoryLinks=[
  ['new','חדשים באתר'],
   ['', 'הכל'],
  ['rings','טבעות'],
  ['earrings','עגילים'],
  ['necklaces','שרשראות'],
  ['bracelets','צמידים']
] as const;

 const goCategory=(key:string)=>{
  navigate(key?`/catalog?category=${key}`:'/catalog');
 };

 return <>
  <nav className="breadcrumbs container mp5-breadcrumbs">
   <a href="/" onClick={e=>{e.preventDefault();navigate('/')}}>בית</a>
   <span>›</span>
   <strong>{title}</strong>
  </nav>

  <section className={`category-hero mp5-hero hero-unified ${category ? `category-hero-${category}` : 'category-hero-all'}`}>
  <img src={heroImage} alt={title} />
  
  <div className="container hero-copy">
    <span className="hero-eyebrow">{heroEyebrow}</span>
    <h1>{title}</h1>
    <p>{intro}</p>
    <a className="hero-shop-cta" href="#shop-products">לגלות את הקולקציה</a>
  </div>
</section>

  <nav className="mp5-category-nav" aria-label="קטגוריות מוסונייט">
   <div className="container">
    {categoryLinks.map(([key,label])=><button type="button" key={key||'all'} className={category===key?'active':''} onClick={()=>goCategory(key)}>{label}</button>)}
</div>
  </nav>

  <section className="mp5-catalog" id="shop-products">
   <div className="container">

    <header className="mp5-toolbar">
     <div>
      <span className="mp5-eyebrow">MOISSANITE COLLECTION</span>
      <h2>{title}</h2>
      <p data-mielley-search-term={q.trim()} data-mielley-results-count={items.length}><b>{items.length}</b> תכשיטים נמצאו</p>
     </div>

     <div className="mp5-toolbar-actions">
      <button className="mp5-filter-mobile" type="button" onClick={()=>setMobileFilters(!mobileFilters)}>☰ סינון</button>
      <label className="mp5-sort">
       <span>מיון</span>
       <select value={sort} onChange={e=>setSort(e.target.value)}>
        <option value="featured">מומלצים</option>
        <option value="new">חדשים</option>
        <option value="low">מחיר: מהנמוך</option>
        <option value="high">מחיר: מהגבוה</option>
       </select>
      </label>
     </div>
    </header>

    {hasFilters&&<div className="mp5-active-filters">
     <span>פילטרים פעילים</span>
     {q&&<button onClick={()=>setQ('')}>חיפוש: {q} ×</button>}
     {shape&&<button onClick={()=>setShape('')}>{shape} ×</button>}
     {stoneSize&&<button onClick={()=>setStoneSize('')}>גודל {stoneSize} ×</button>}
     {stock&&<button onClick={()=>setStock(false)}>במלאי ×</button>}
     {ready&&<button onClick={()=>setReady(false)}>מוכן למשלוח ×</button>}
     {sale&&<button onClick={()=>setSale(false)}>SALE ×</button>}
     {tennis&&<button onClick={()=>setTennis(false)}>TENNIS ×</button>}
     {selected&&<button onClick={()=>setSelected(false)}>נבחרים ×</button>}
     {max<999999&&<button onClick={()=>setMax(999999)}>עד {money(sliderValue)} ×</button>}
     <button className="clear" onClick={clear}>ניקוי הכל</button>
    </div>}

    <div className="mp5-layout mp84-catalog-layout">
     <aside className={`mp5-filters ${mobileFilters?'open':''}`}>
      <div className="mp5-filter-head">
       <div><span>FILTER BY</span><h3>סינון הקולקציה</h3></div>
       <button type="button" onClick={clear}>ניקוי</button>
      </div>

      <div className="mp5-filter-group">
       <label>חיפוש</label>
       <div className="mp5-search"><input value={q} onChange={e=>{setQ(e.target.value);setVisible(24)}} placeholder="שם, סגנון, צורה או מק״ט"/><span>⌕</span></div>
      </div>

      <div className="mp5-filter-group">
       <label>צורת האבן</label>
       <select value={shape} onChange={e=>{setShape(e.target.value);setVisible(24)}}>
        <option value="">כל הצורות</option>
        {shapes.map(s=><option key={s} value={s}>{cleanLabel(String(s))}</option>)}
       </select>
      </div>

      {stoneSizes.length>0&&<div className="mp5-filter-group">
       <label>גודל האבן</label>
       <select value={stoneSize} onChange={e=>{setStoneSize(e.target.value);setVisible(24)}}>
        <option value="">כל הגדלים</option>
        {stoneSizes.map(s=><option key={s} value={s}>{s}</option>)}
       </select>
      </div>}

      <div className="mp5-filter-group">
       <div className="mp5-price-label"><label>טווח מחיר</label><strong>{max>=999999?'ללא הגבלה':`עד ${money(sliderValue)}`}</strong></div>
       <input className="mp5-range" type="range" min="500" max={catalogCeiling} step="250" value={sliderValue} onChange={e=>{setMax(Number(e.target.value));setVisible(24)}}/>
       <div className="mp5-range-ends"><span>{money(500)}</span><span>{money(catalogCeiling)}</span></div>
      </div>

      <div className="mp5-filter-group mp5-checks">
       <label><input type="checkbox" checked={stock} onChange={e=>{setStock(e.target.checked);setVisible(24)}}/><span><b>במלאי</b><small>תכשיטים זמינים בלבד</small></span></label>
       <label><input type="checkbox" checked={selected} onChange={e=>{setSelected(e.target.checked);setVisible(24)}}/><span><b>נבחרים</b><small>פריטים מומלצים מהקולקציה</small></span></label>
       <label><input type="checkbox" checked={sale} onChange={e=>{setSale(e.target.checked);setVisible(24)}}/><span><b>SALE</b><small>מחיר מיוחד</small></span></label>
      </div>

      {mobileFilters&&<button className="mp5-show-results" onClick={()=>setMobileFilters(false)}>הצגת {items.length} תכשיטים</button>}
     </aside>

     <main className="mp5-results">
      {loading
       ?<div className="empty"><h2>טוען קטלוג…</h2></div>
       :items.length
        ?<div className="product-grid mp5-product-grid mp84-product-grid">{items.slice(0,visible).map(p=><ProductCard key={p.id} product={p}/>)}</div>
        :<div className="mp5-empty">
          <span>NO RESULTS</span>
          <h3>לא מצאנו תכשיטים מתאימים</h3>
          <p>נסו להסיר אחד מהפילטרים או לבחור טווח מחיר רחב יותר.</p>
          <button onClick={clear}>ניקוי כל הפילטרים</button>
         </div>}

      {visible<items.length&&<div className="mp5-load-more">
       <button onClick={()=>setVisible(v=>v+24)}>הצגת תכשיטים נוספים</button>
       <span>{Math.min(visible,items.length)} מתוך {items.length}</span>
      </div>}
     </main>
    </div>
   </div>
  </section>

  <section className="mp5-seo">
   <div className="container narrow">
    <span>ABOUT MOISSANITE</span>
    <h2>{title}</h2>
    <p>{intro}</p>
    <p>בכל דף מוצר תוכלו לראות את גודל האבן, הצורה, מידת הטבעת כאשר רלוונטי, זמני האספקה ופרטי 925 Sterling Silver ו־Rhodium Finish.</p>
   </div>
  </section>
 </>;
}
